import { useTranslation } from "react-i18next";
import { useState } from "react";

import apiClient from "../../lib/common/apiClient";

import DeleteRecordButtonLayout from "../layout/DeleteRecordButtonLayout";
import ConfirmDialog from "../dialogTypes/ConfirmDialog";
import failureKey from "../../lib/common/failureKey";
/**
 * @COMPONENT_NAME CompanyDeleteButton
 * @DESCRIPTION 회사 레코드 삭제를 위한 버튼 컴포넌트. 확인 다이얼로그를 표시하고 일괄 삭제 작업을 처리합니다.
 *
 * @PROPS
 *    - {string} id - 버튼 식별자
 *    - {Object} schema - 폼 스키마 구성
 *    - {Object} grid - 그리드 컴포넌트 참조
 *    - {Object} refresh - 그리드 새로고침 함수
 *    - {Function} makeItemName - 확인용 아이템명 생성 함수
 *    - {Function} onSuccess - 삭제 성공 시 호출
 *    - {Function} onFailure - 삭제 실패 시 호출
 *    - {Function} onPartialFailure - 부분 삭제 발생 시 호출
 *    - {Function} modifyUrlCallback - URL 수정 콜백
 *
 * @PASSTHROUGH_PROPS
 *    - {Object} others - 버튼 레이아웃에 전달되는 추가 props
 *
 * @CALLBACKS
 *    - {Function} onOpen - 버튼 클릭 시 호출
 *    - {Function} onClose - 다이얼로그 닫힐 때 호출
 *    - {Function} onDeleteOk - 삭제 확인 시 호출
 *
 * @EXAMPLES
 * ```jsx
 * <CompanyDeleteButton
 *   id="company-delete"
 *   schema={schema}
 *   grid={grid}
 *   refresh={refresh}
 *   onSuccess={handleSuccess}
 *   onFailure={handleFailure}
 * />
 * ```
 */
function CompanyDeleteButton(props) {
    const { t } = useTranslation();

    const {
        id,
        schema,
        grid,
        refresh,
        makeItemName,
        onSuccess,
        onFailure,
        onPartialFailure,
        modifyUrlCallback,
        ...others
    } = props;

    const [open, setOpen] = useState(false);
    const [confirmMsg, setConfirmMsg] = useState();
    const [confirmData, setConfirmData] = useState();

    const makeReasonStr = (reasons) => {
        let reasonStr = " [";

        for (const index in reasons) {
            if (index > 0) {
                reasonStr += ", ";
            }

            reasonStr += reasons[index];
        }

        reasonStr += "]";

        return reasonStr;
    };

    const onOpen = () => {
        let deleteData = [];
        let deleteItemStr = "[";

        let firstRow = true;

        for (const item of grid.selectedItems) {
            if (!firstRow) {
                deleteItemStr += ", ";
            }

            if (makeItemName) {
                deleteItemStr += makeItemName(item);
            } else {
                let itemDetail = "{";

                let firstCol = true;

                for (let key of schema.keys) {
                    if (!firstCol) {
                        itemDetail += ", ";
                    }

                    itemDetail += t(`columnName.companyCode`) + ": " + item[key];

                    firstCol = false;
                }

                itemDetail += "}";

                deleteItemStr += itemDetail;
            }

            firstRow = false;

            deleteData.push({ ...item });
        }

        deleteItemStr += "]";

        setConfirmMsg(
            t("dialog.msg.confirm.delete", { type: t(`tableName.MomSysUdtCompany`), instance: deleteItemStr })
        );
        setConfirmData(deleteData);

        setOpen(true);
    };

    const onClose = () => {
        setOpen(false);
    };

    const onDeleteOk = (requestData) => {
        let url = "/api/" + schema.name;

        if (modifyUrlCallback) {
            url = modifyUrlCallback(url);
        }

        apiClient
            .bulkDel(url, requestData)
            .then((result) => {
                if (result.data.fails.length === 0) {
                    if (refresh) {
                        refresh.refresh();
                    }

                    onSuccess(
                        t("success.deleteSuccess", {
                            table: schema.name,
                            deleteCount: result.data.successes.length.toString(),
                        })
                    );
                } else if (result.data.successes.length === 0) {
                    let reasons = makeReasonStr(result.data.reasons);
                    onFailure(t(failureKey("delete", result.data.statuses), { reason: reasons }));
                } else {
                    let reasons = makeReasonStr(result.data.reasons);

                    if (refresh) {
                        refresh.refresh();
                    }

                    onPartialFailure(
                        t("warning.partialDeleteFailure", {
                            success: result.data.successes.length,
                            fail: result.data.fails.length,
                            reason: reasons,
                        })
                    );
                }
            })
            .catch((reason) => {
                console.error(reason);
                onFailure(t(failureKey("delete", reason.response?.status), { reason: reason.response ? reason.response.data : reason }));
            });
    };

    return (
        <>
            <DeleteRecordButtonLayout id={id + "delete-button"} onClick={onOpen} {...others} />
            <ConfirmDialog
                id={id + "-delete-confirm-dialog"}
                title={schema ? t(`dialog.title.MomSysUdtCompany.delete`) : ""}
                msg={confirmMsg}
                data={confirmData}
                open={open}
                onClose={onClose}
                onOk={onDeleteOk}
                onCancel={onClose}
            />
        </>
    );
}

export default CompanyDeleteButton;
